import { trpc } from "@client/trpc";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";

type RepoCommitHashes = {
  repoCommitHash: string; // The commit hash of the whole repo
  engineCombinedCommitHash: string; // The combined commit hashes of engine/round and engine/core.ts
};

export function OutOfDateVersionWarning() {
  // FIXME: After we load, we ask the server what the latest version is, and
  // assume that must be our own version number. But that means that if we
  // somehow got a stale cached page, or are unlucky enough to load during the
  // window where the version is changing, we could think we're on a new version
  // when we're not.
  const hasDeliveredMessage = useRef(false);
  const { repoIsOutOfDate } = useIsOutOfDate();

  if (repoIsOutOfDate) {
    // if the version has changed but we've already delivered the message, do nothing
    if (hasDeliveredMessage.current) {
      return;
    }

    // the version has changed and the user has not seen the message yet
    hasDeliveredMessage.current = true;
    toast.info(
      "There is a new version of this app. Please refresh the page to get the latest version.",
      {
        autoClose: false,
      },
    );
  }

  return <></>;
}

export function useIsOutOfDate(): {
  repoIsOutOfDate: boolean;
  engineIsOutOfDate: boolean;
} {
  const initialVersion = useRef<RepoCommitHashes | null>(null);
  const repoIsOutOfDate = useRef(false);
  const engineIsOutOfDate = useRef(false);

  const recheckVersion = useCallback(async () => {
    const buildId = await getBuildId();
    // request failed, do nothing
    if (!buildId) {
      return;
    }

    const { repoCommitHash: currentVersion, engineCombinedCommitHash } =
      buildId;

    // first successful request, set the initial version
    if (initialVersion.current == null) {
      initialVersion.current = buildId;
      return;
    }

    if (initialVersion.current.repoCommitHash !== currentVersion) {
      repoIsOutOfDate.current = true;
    }

    if (
      initialVersion.current.engineCombinedCommitHash !==
      engineCombinedCommitHash
    ) {
      engineIsOutOfDate.current = true;
    }
  }, []);

  useEffect(() => {
    void recheckVersion();
    // Recheck version when you focus to the tab
    document.addEventListener("visibilitychange", recheckVersion);

    // Set a timer to recheck the version every 5 minutes
    const timer = window.setInterval(
      () => {
        void recheckVersion();
      },
      5 * 60 * 1000,
    );
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", recheckVersion);
    };
  }, [recheckVersion]);

  return {
    repoIsOutOfDate: repoIsOutOfDate.current,
    engineIsOutOfDate: engineIsOutOfDate.current,
  };
}

async function getBuildId(): Promise<RepoCommitHashes | null> {
  try {
    return await trpc().getBuildId.query();
  } catch {
    return null;
  }
}
